import { type ReactNode, Suspense } from "react";
import { render, unmountComponentAtNode } from "react-dom";
import { Provider } from "react-redux";
import { theme } from "~/app/theme";
import { GlobalStyles as MuiGlobalStyles, ThemeProvider, type Theme } from "~/common/mui";
import { getStore } from "~/app/store/getStore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { externalContentStyles } from "~/common/styles/ExternalContentGlobalStyles";
import { printStyles } from "~/common/styles/PrintGlobalStyles";
import { vendorAndIframeStyles } from "~/common/styles/VendorAndIframeGlobalStyles";

const globalStyles = (theme: Theme) => ({
  ...printStyles(theme),
  ...externalContentStyles(theme),
  ...vendorAndIframeStyles(theme),
});

window.SCHA = window.SCHA || {};
window.SCHA.cmps = {};

const tryCreate = (componentName: string, rootElement: HTMLElement, props: unknown) => {
  const factory = window.SCHA.cmps[componentName];
  if (factory) {
    try {
      render(renderRoot(factory(props), componentName), rootElement);
    } catch (e) {
      if (e instanceof Error) {
        console.error(`Error when rendering component at ${rootElement.id}: ${e.message}`);
        console.error(e.stack);
      }
    }
  } else {
    console.error(`Could not find a component registered as ${componentName}`);
  }
};
window.SCHA.createComponentInElement = tryCreate;

window.SCHA.unmountComponentAtNode = unmountComponentAtNode;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function registerComponentFactory(name: string, factory: (props: any) => ReactNode) {
  if (import.meta.env.DEV) {
    if (window.SCHA.cmps[name]) {
      console.warn(`Component registry: Overriding an already registered component at ${name}`);
    }
  }

  window.SCHA.cmps[name] = factory;
}

const renderRoot = (node: ReactNode, componentName: string) => {
  return (
    <Suspense fallback={null}>
      <ThemeProvider theme={theme}>
        <MuiGlobalStyles styles={globalStyles} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Provider store={getStore()}>{node}</Provider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};
